import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { userManager } from '../config/keycloak';

async function redirectToLoginAfterSessionLoss(): Promise<void> {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname + window.location.search + window.location.hash;
  if (path.startsWith('/callback') || path.startsWith('/silent-renew') || path.startsWith('/login')) {
    return;
  }
  try {
    await userManager.removeUser();
  } catch {
    /* ignore */
  }
  sessionStorage.setItem('keycloak_redirect_path', path);
  localStorage.setItem('keycloak_redirect_path', path);
  try {
    await userManager.signinRedirect();
  } catch {
    window.location.assign('/login');
  }
}
import { API_BASE_URL } from '../config/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Request interceptor - add JWT token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      let user = await userManager.getUser();
      
      // If token is expired, try to refresh it silently
      if (user?.expired) {
        console.log('Token expired, attempting silent refresh...');
        try {
          user = await userManager.signinSilent();
        } catch (refreshError) {
          console.warn('Silent refresh failed, user may need to re-login:', refreshError);
          // Clear stale user data
          await userManager.removeUser();
        }
      }
      
      if (user?.access_token && config.headers) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
      }
    } catch (error) {
      console.error('Error getting user token:', error);
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Helper function to check if error is a network/connection error
const isNetworkError = (error: AxiosError): boolean => {
  return (
    !error.response && 
    (error.code === 'ERR_NETWORK' || 
     error.code === 'ECONNREFUSED' || 
     error.message === 'Network Error' ||
     error.message.includes('CONNECTION_REFUSED'))
  );
};

// Response interceptor - handle token refresh and network errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    // Handle authentication errors (401)
    if (error.response?.status === 401 && originalRequest) {
      try {
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const isAuthCallbackRoute =
          pathname.startsWith('/callback') || pathname.startsWith('/silent-renew');
        const isLoginRoute = pathname.startsWith('/login');

        // Avoid auth churn while OIDC callback/silent renew is being processed.
        if (isAuthCallbackRoute || isLoginRoute) {
          return Promise.reject(error);
        }

        // Retry once with a freshly renewed token instead of forcing a redirect loop.
        if (originalRequest._retry) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;

        const refreshedUser = await userManager.signinSilent();
        if (refreshedUser?.access_token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${refreshedUser.access_token}`;
        }
        return apiClient(originalRequest);
      } catch (authError) {
        console.warn('Silent refresh retry failed after 401:', authError);
        // Stale or missing session (common in embedded browsers after token expiry).
        await redirectToLoginAfterSessionLoss();
      }
      return Promise.reject(error);
    }
    
    // For 403 errors, log but don't automatically redirect
    // (403 might be legitimate authorization failure, not authentication issue)
    if (error.response?.status === 403) {
      console.error('Received 403 Forbidden:', error.response?.data);
    }
    
    // Don't log network errors (expected when backend is down)
    // They will be handled gracefully by the hooks/components
    if (isNetworkError(error)) {
      // Silently reject - components will handle gracefully
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

